import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Pagination, TextField, Button, useTheme, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import { ArrowForwardRounded, Visibility, CheckCircle, Cancel } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  getFinancialDashboard,
  getShippingPayments,
  approveShippingPayment,
  rejectShippingPayment,
  listBranchExpenses,
  listServiceCharges,
} from "api/financial";
import { ListPackageChangeRequests } from "api/packageChangeRequest";
import ShippingApprovalDetailDialog from "./ShippingApprovalDetailDialog";
import { SYM } from "./receiptParts";
import RejectReasonDialog from "./RejectReasonDialog";
import TopupApprovalsTab from "./TopupApprovalsTab";
import BranchExpenseApprovalsTab from "./BranchExpenseApprovalsTab";
import BranchServiceChargeApprovalsTab from "./BranchServiceChargeApprovalsTab";
import PackageChangeRequests from "scenes/packageChangeRequests";
import BentoTabs from "components/bento/BentoTabs";
import BentoCard from "components/bento/BentoCard";
import PageShell from "components/bento/PageShell";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";
import { user } from "localStorage";
import { getVisibleTabs, hasPageAccess } from "utils/pagePermissions";
import { useUserRefreshTick } from "hooks/useUserRefreshTick";

const ALL_APPROVALS_TABS = [
  { key: "shipping", slug: "financial/approvals/shipping" },
  { key: "topups", slug: "financial/approvals/topups" },
  { key: "expenses", slug: "financial/approvals/expenses" },
  { key: "serviceCharges", slug: "financial/approvals/service-charges" },
  // Parcel edit/cancel requests — gated by the approver sub-slug of its own
  // page (packages/change-requests/approve), not a financial/approvals tab slug.
  // The standalone change-requests page stays for requesters to track theirs.
  { key: "changeRequests", page: "packages/change-requests/approve" },
];

const METHOD_LABEL = { cash: "Cash", bank_transfer: "Bank transfer" };
const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CARD_SHADOW = "0 1px 2px rgba(20,18,30,.04),0 16px 32px -24px rgba(20,18,30,.18)";

const SHIPPING_STATUS_OPTIONS = ["pending_approval", "completed", "rejected", "all"];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function ShippingApprovals({ onChanged }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [statusFilter, setStatusFilter] = useState("pending_approval");
  const [customer, setCustomer] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const res = await getShippingPayments(statusFilter, p, 20, "", dateFrom, dateTo, customer?._id || "");
    setRows(res && res.success ? res.data : []);
    setPagination((res && res.pagination) || { page: 1, pages: 1, total: 0 });
    setLoading(false);
  }, [statusFilter, customer, dateFrom, dateTo]);
  useEffect(() => { setPage(1); load(1); }, [statusFilter, customer, dateFrom, dateTo, load]);

  const handlePageChange = (e, p) => { setPage(p); load(p); };

  const handleApprove = async (id) => {
    setBusy(true);
    const res = await approveShippingPayment(id);
    setBusy(false);
    Swal.fire({ icon: res.success ? "success" : "error", text: res.message, timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
    if (res.success) setDetailId(null);
    load(page);
    onChanged && onChanged();
  };
  const doReject = async (reason) => {
    setBusy(true);
    const res = await rejectShippingPayment(rejectId, reason || "");
    setBusy(false);
    Swal.fire({ icon: res.success ? "success" : "error", text: res.message, timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
    if (res.success) {
      setRejectId(null);
      setDetailId(null);
    }
    load(page);
    onChanged && onChanged();
  };

  return (
    <>
      <Box
        sx={{
          display: { xs: "grid", md: "flex" },
          gridTemplateColumns: { xs: "1fr 1fr" },
          alignItems: "center",
          gap: "12px",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1" }, width: { xs: "100%", md: "auto" }, minWidth: { md: 220 }, flex: { md: "1 1 220px" } }}>
          <CustomerSearchAutocomplete value={customer} onChange={setCustomer} label={t("financial.customer")} size="small" />
        </Box>
        <FormControl size="small" sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 170 } }}>
          <InputLabel>{t("financial.status")}</InputLabel>
          <Select value={statusFilter} label={t("financial.status")} onChange={(e) => setStatusFilter(e.target.value)}>
            {SHIPPING_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{t(`financial.${s === "pending_approval" ? "pending" : s}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="datetime-local" label={t("financial.dateFrom")} sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 200 } }}
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="datetime-local" label={t("financial.dateTo")} sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 200 } }}
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined" color="secondary"
          sx={{ width: { xs: "100%", md: "auto" } }}
          onClick={() => { setCustomer(null); setDateFrom(""); setDateTo(""); }}
        >
          {t("common.clear")}
        </Button>
        <Box sx={{ gridColumn: { xs: "1 / -1" }, ml: { md: "auto" }, textAlign: { xs: "right" }, fontSize: "12.5px", color: "#a8a3b4", fontWeight: 600 }}>
          {pagination.total || 0} {t("financial.results")}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t(statusFilter === "pending_approval" ? "financial.noPending" : "financial.noData")}
        </Typography>
      ) : isMobile ? (
      <Grid container spacing={2}>
        {rows.map((sp) => {
          const methods = (sp.lines || [])
            .map((l) => METHOD_LABEL[l.method] || l.method)
            .join(" + ") || "—";
          return (
            <Grid item xs={12} md={6} key={sp._id}>
              <Card
                elevation={0}
                onClick={() => setDetailId(sp._id)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "20px",
                  border: "1px solid #ece9f1",
                  boxShadow: CARD_SHADOW,
                  transition: "transform .18s, box-shadow .18s, border-color .18s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: "#d9cffb",
                    boxShadow: "0 1px 2px rgba(20,18,30,.05),0 22px 40px -22px rgba(86,40,160,.4)",
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  {/* header: fee due + reason */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#a8a3b4" }}>
                        {t("financial.feeDue")}
                      </Typography>
                      <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 28, lineHeight: 1.15, color: "#6d28d9" }}>
                        ฿{fmt(sp.feeTHB)}
                      </Typography>
                    </Box>
                    {sp.status === "pending_approval" && sp.approvalReason ? (
                      <Chip size="small" color="warning" label={t(`financial.reason.${sp.approvalReason}`)} sx={{ fontWeight: 700, flexShrink: 0 }} />
                    ) : (
                      <Chip size="small" color={sp.status === "completed" ? "success" : "error"} label={t(`financial.${sp.status === "completed" ? "completed" : "rejected"}`)} sx={{ fontWeight: 700, flexShrink: 0 }} />
                    )}
                  </Box>

                  {/* customer */}
                  {sp.customer && (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 12, background: "linear-gradient(135deg,#7c3aed,#5b21b6)" }}>
                        {initials(sp.customer.name)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#2c2937", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {sp.customer.name || "—"}
                        </Typography>
                        {sp.customer.whatsapp && (
                          <Typography sx={{ fontFamily: MONO, fontSize: 12, color: "#6b6775" }}>
                            {sp.customer.whatsapp}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* meta tiles */}
                  <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                    {[
                      { label: t("financial.station"), value: sp.stationId },
                      { label: t("financial.parcels"), value: sp.packageIds?.length || 0 },
                      {
                        label: t("financial.walletPortion"),
                        value: `${SYM[sp.walletCurrency || "THB"] || ""}${fmt(sp.walletPortionAmount || sp.walletPortionTHB)}`,
                      },
                      { label: t("financial.paymentLines"), value: methods },
                    ].map((m, i) => (
                      <Box key={i} sx={{ p: 1.25, borderRadius: "12px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#a8a3b4" }}>
                          {m.label}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#2c2937", mt: 0.25, wordBreak: "break-word" }}>
                          {m.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* footer cue */}
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1eff5", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5, color: "#6d28d9" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{t("financial.viewDetails")}</Typography>
                    <ArrowForwardRounded sx={{ fontSize: 16 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      ) : (
        <BentoCard bodySx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("financial.receipt")}</TableCell>
                  <TableCell>{t("financial.customer")}</TableCell>
                  <TableCell>{t("financial.feeDue")}</TableCell>
                  <TableCell>{t("financial.status")}</TableCell>
                  <TableCell>{t("financial.reasonHeader")}</TableCell>
                  <TableCell>{t("financial.station")}</TableCell>
                  <TableCell>{t("financial.date")}</TableCell>
                  <TableCell>{t("financial.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((sp) => (
                  <TableRow key={sp._id} hover>
                    <TableCell>
                      <Tooltip title={sp.receiptId || ""}>
                        <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: "0.75rem" }}>{sp.receiptId}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">{sp.customer?.name || "—"}</Typography>
                      <Typography variant="body2" color="text.secondary">{sp.customer?.whatsapp}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">฿{fmt(sp.feeTHB)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={sp.status === "completed" ? "success" : sp.status === "rejected" ? "error" : "warning"}
                        label={t(`financial.${sp.status === "pending_approval" ? "pending" : sp.status}`)}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      {sp.status === "pending_approval" && sp.approvalReason ? (
                        <Chip size="small" variant="outlined" color="warning" label={t(`financial.reason.${sp.approvalReason}`)} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{sp.stationId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(sp.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button variant="outlined" color="secondary" size="small" startIcon={<Visibility />} onClick={() => setDetailId(sp._id)}>
                          {t("financial.details")}
                        </Button>
                        {sp.status === "pending_approval" && (
                          <>
                            <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleApprove(sp._id)}>
                              {t("financial.approve")}
                            </Button>
                            <Button size="small" variant="contained" color="error" startIcon={<Cancel />} onClick={() => setRejectId(sp._id)}>
                              {t("financial.reject")}
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </BentoCard>
      )}

      {pagination.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination color="secondary" count={pagination.pages} page={page} onChange={handlePageChange} />
        </Box>
      )}

      <ShippingApprovalDetailDialog
        open={!!detailId}
        paymentId={detailId}
        busy={busy}
        onClose={() => setDetailId(null)}
        onApprove={handleApprove}
        onReject={(id) => setRejectId(id)}
      />

      <RejectReasonDialog
        open={!!rejectId}
        busy={busy}
        onClose={() => setRejectId(null)}
        onConfirm={doReject}
      />
    </>
  );
}

export default function FinancialApprovals() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  useUserRefreshTick(); // re-render when the cached profile refreshes — see hooks/useUserRefreshTick.js

  const visibleSlugs = getVisibleTabs(user(), "financial/approvals").map((tb) => tb.slug);
  const tabDefs = ALL_APPROVALS_TABS.filter((tb) =>
    tb.page ? hasPageAccess(user(), tb.page) : visibleSlugs.includes(tb.slug)
  );

  // Deep-link support: ?sub=topups / ?sub=expenses (e.g. from a wallet top-up or
  // branch expense approval notification) opens that sub-tab directly, if visible
  // to this role.
  const [sub, setSub] = useState(() => {
    const idx = tabDefs.findIndex((tb) => tb.key === searchParams.get("sub"));
    return idx >= 0 ? idx : 0;
  });
  const [counts, setCounts] = useState({ pendingApprovals: 0, pendingTopups: 0, pendingExpenses: 0, pendingServiceCharges: 0, pendingChangeRequests: 0 });

  const TAB_LABEL = {
    shipping: t("financial.approvalsShipping"),
    topups: t("financial.approvalsTopups"),
    expenses: t("financial.approvalsExpenses"),
    serviceCharges: t("financial.approvalsServiceCharges"),
    changeRequests: t("financial.approvalsChangeRequests"),
  };
  const TAB_BADGE = {
    shipping: counts.pendingApprovals,
    topups: counts.pendingTopups,
    expenses: counts.pendingExpenses,
    serviceCharges: counts.pendingServiceCharges,
    changeRequests: counts.pendingChangeRequests,
  };
  const tabs = tabDefs.map((tb) => ({
    key: tb.key,
    label: TAB_LABEL[tb.key],
    badge: TAB_BADGE[tb.key],
  }));

  const loadCounts = useCallback(async () => {
    const [dashRes, expRes, scRes, crRes] = await Promise.all([
      getFinancialDashboard(),
      listBranchExpenses({ status: "pending_approval", page: 1, limit: 1 }),
      listServiceCharges({ status: "pending_approval", page: 1, limit: 1 }),
      hasPageAccess(user(), "packages/change-requests/approve")
        ? ListPackageChangeRequests({ status: "pending", page: 0, pageSize: 1 }).catch(() => null)
        : null,
    ]);
    setCounts({
      pendingApprovals: (dashRes && dashRes.success && dashRes.data.pendingApprovals) || 0,
      pendingTopups: (dashRes && dashRes.success && dashRes.data.pendingTopups) || 0,
      pendingExpenses: (expRes && expRes.success && expRes.pagination?.total) || 0,
      pendingServiceCharges: (scRes && scRes.success && scRes.pagination?.total) || 0,
      pendingChangeRequests: (crRes && crRes.success && crRes.total) || 0,
    });
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  useEffect(() => {
    const subParam = searchParams.get("sub");
    if (subParam) {
      const idx = tabDefs.findIndex((tb) => tb.key === subParam);
      if (idx >= 0) setSub(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (sub >= tabDefs.length) setSub(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabDefs.length]);

  const activeKey = tabDefs[sub]?.key;

  return (
    <PageShell title={t("financial.approvals")}>
      <BentoTabs
        value={sub}
        onChange={setSub}
        sx={{ display: "inline-flex", mb: "22px" }}
        tabs={tabs}
      />
      {activeKey === "shipping" && <ShippingApprovals onChanged={loadCounts} />}
      {activeKey === "topups" && <TopupApprovalsTab onChanged={loadCounts} />}
      {activeKey === "expenses" && <BranchExpenseApprovalsTab onChanged={loadCounts} />}
      {activeKey === "serviceCharges" && <BranchServiceChargeApprovalsTab onChanged={loadCounts} />}
      {activeKey === "changeRequests" && <PackageChangeRequests embedded onChanged={loadCounts} />}
    </PageShell>
  );
}
